import React, { useState } from "react";
import { setNotification } from "features/Notification/notificationSlice";
import API from "services/axios";
import authHeader from "services/auth-header";
import { useDispatch, useSelector } from "react-redux";
import useInbox from "../InboxTab/InboxContext";
import EmailActionIcon from "./EmailActionIcon";
import { Dropdown } from "react-bootstrap";
import Spinner from "components/Spinner";

export default function EmailBulkActions({
  refreshFunc,
  refreshFuncParams,
  selectedEmails,
  className = "",
}) {
  /* ******************** All States ************************* */
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const {
    selectedLabel,
    labels,
    fromEmail,
    messageId,
    setMessageId,
    handleBulkReadStatus,
    handleBulkMove,
    emailsList,
    emailsHistoryList,
  } = useInbox();
  const [loading, setLoading] = useState(false);

  /* ******************** Bulk Actions ************************* */
  const handleBulkReadStatusChange = async (isRead) => {
    const messageIdsArray = selectedEmails ? selectedEmails : [messageId];
    if (messageIdsArray.length === 0) {
      dispatch(
        setNotification({
          message: "Please select at least one email",
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      let success = await handleBulkReadStatus(messageIdsArray, isRead);
      if (success) {
        // refreshLabels();
        if (!selectedEmails) {
          setMessageId(null);
        }
        await refreshFunc(refreshFuncParams);
        dispatch(
          setNotification({
            message: `Emails marked as ${isRead ? "read" : "unread"}`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to update email status",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Trash All Emails in a thread
  const handleBulkDeleteThreads = async () => {
    const messageIdsArray = selectedEmails ? selectedEmails : [messageId];
    if (messageIdsArray.length === 0) {
      dispatch(
        setNotification({
          message: "Please select at least one email",
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      let success = await API.post(
        `/emails/delete-messages`,
        { messageIdsArray },
        { headers: authHeader() },
      ).then((res) => res.data.success);
      if (success) {
        // refreshLabels();
        if (!selectedEmails) {
          setMessageId(null);
        }
        await refreshFunc(refreshFuncParams);
        dispatch(
          setNotification({
            message: `Emails marked as deleted`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to update email status",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // move email to different label
  const handleBulkMoveThreads = async (labelId) => {
    const messageIdsArray = selectedEmails ? selectedEmails : [messageId];
    if (messageIdsArray.length === 0) {
      dispatch(
        setNotification({
          message: "Please select at least one email",
          type: "error",
        }),
      );
      return;
    }

    if (!labelId) {
      dispatch(
        setNotification({
          message: "Invalid label selected",
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      let success = await handleBulkMove(messageIdsArray, labelId);
      if (success) {
        if (!selectedEmails) {
          setMessageId(null);
        }
        await refreshFunc(refreshFuncParams);
        dispatch(
          setNotification({
            message: `Emails moved successfully`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to move emails",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Get filtered Email Ids for archiving
  const getArchiveEmailIds = (messageIdsArray) => {
    // selected correct emails to check against
    let _emailsList = fromEmail ? emailsHistoryList : emailsList;

    // Filter out emails with inbox label than apply archive label
    const inboxEmails = messageIdsArray.filter((id) => {
      const email = _emailsList.find((e) => e.id === id);
      return email?.folders?.includes("INBOX");
    });
    if (inboxEmails.length === 0) {
      dispatch(
        setNotification({
          message: "No emails with inbox label selected",
          type: "error",
        }),
      );
      return;
    }
    return inboxEmails;
  };

  // Get filtered Email Ids for unarchiving
  const getUnArchiveEmailIds = (messageIdsArray) => {
    // selected correct emails to check against
    let _emailsList = fromEmail ? emailsHistoryList : emailsList;

    // Filter out emails with archived label than remove archive label
    const archivedEmails = messageIdsArray.filter((id) => {
      const email = _emailsList.find((e) => e.id === id);
      // check if email does not have INBOX label
      return !email?.folders?.includes("INBOX");
    });
    if (archivedEmails.length === 0) {
      dispatch(
        setNotification({
          message: "No archived emails selected",
          type: "error",
        }),
      );
      return;
    }
    return archivedEmails;
  };

  const handleBulkMoveToArchive = async (labelId) => {
    let isArchived = labelId === "ARCHIVED" ? true : false;

    let isEmailDetailsPage = !selectedEmails;

    let _emailsList = fromEmail ? emailsHistoryList : emailsList;

    let messageIdsArray = [];

    if (!isEmailDetailsPage) {
      if (selectedEmails?.length === 0) {
        dispatch(
          setNotification({
            message: "Please select at least one email",
            type: "error",
          }),
        );
        return;
      }
      let emailsId = [];
      if (isArchived) {
        const archiveEmailIds = getArchiveEmailIds(selectedEmails);
        emailsId = archiveEmailIds ? archiveEmailIds : [];
      } else {
        const unArchiveEmailIds = getUnArchiveEmailIds(selectedEmails);
        emailsId = unArchiveEmailIds ? unArchiveEmailIds : [];

        console.log("unArchived Emails:", emailsId);
      }
      if (emailsId.length === 0) {
        return;
      }
      messageIdsArray = emailsId;
    } else {
      // filter out email from emailHistoryList with messageId and check if it has INBOX label or not based on archive or unarchive action
      const email = _emailsList.find((e) => e.id === messageId);
      if (isArchived && !email?.folders?.includes("INBOX")) {
        dispatch(
          setNotification({
            message: "This email cannot be archived",
            type: "error",
          }),
        );
        return;
      }
      if (!isArchived && email?.folders?.includes("INBOX")) {
        dispatch(
          setNotification({
            message: "This email is not archived",
            type: "error",
          }),
        );
        return;
      }
      messageIdsArray = [messageId];
    }

    try {
      setLoading(true);
      let { data } = await API.post(
        "/emails/mark-archived-unarchived",
        {
          messageIds: messageIdsArray,
          isArchived,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        if (!selectedEmails) {
          setMessageId(null);
        } else {
          await refreshFunc(refreshFuncParams);
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /* ******************** All UseEffects ************************* */

  return (
    <div
      className={["d-flex align-items-center", className].join(" ")}
      style={{ gap: 12 }}
    >
      {loading && <Spinner />}
      {/* Archive */}
      <button
        className="btn btn-sm px-0"
        onClick={() => handleBulkMoveToArchive("ARCHIVED")}
        title="Archive"
      >
        <EmailActionIcon
          name="archive"
          style={{
            fontSize: "23px",
            width: "23px",
          }}
          className="flex-center p-0"
        />
      </button>
      {/* Un Archive */}
      <button
        className="btn btn-sm px-0"
        onClick={() => handleBulkMoveToArchive("UNARCHIVED")}
        title="Move to inbox"
      >
        <EmailActionIcon
          name="unarchive"
          style={{
            fontSize: "23px",
            width: "23px",
          }}
          className="flex-center p-0"
        />
      </button>
      {/* Delete */}
      <button
        className="btn btn-sm px-0 d-flex align-items-center"
        onClick={handleBulkDeleteThreads}
      >
        <EmailActionIcon
          name="delete"
          style={{
            fontSize: "23px",
            width: "23px",
          }}
          className="flex-center p-0"
        />
      </button>

      {/* Mark as Unread */}
      <button
        className="btn btn-sm px-0 d-flex align-items-center"
        onClick={() => handleBulkReadStatusChange(false)}
        title="Mark as Unread"
      >
        <EmailActionIcon
          name="mark_email_unread"
          style={{
            fontSize: "23px",
            width: "23px",
          }}
          className="flex-center p-0"
        />
      </button>
      {/* Mark as Read */}
      {!messageId && (
        <button
          className=" btn btn-sm px-0 d-flex align-items-center"
          onClick={() => handleBulkReadStatusChange(true)}
          title="Mark as Read"
        >
          <EmailActionIcon
            name="drafts"
            style={{
              fontSize: "23px",
              width: "23px",
            }}
            className="flex-center p-0"
          />
        </button>
      )}

      {/* Move Label DropDown */}
      <Dropdown className="ms-0">
        <Dropdown.Toggle
          as={"div"}
          style={{
            cursor: "pointer",
          }}
          className="d-flex align-items-center gap-1 fs-5 py-0"
          variant=""
        >
          <EmailActionIcon
            name="drive_file_move"
            style={{
              fontSize: "23px",
            }}
            className="flex-center p-0"
          />{" "}
        </Dropdown.Toggle>

        <Dropdown.Menu className="py-2 pe-2">
          {/* Inbox, Sent, Trash */}
          {user.roles === "admin" && (
            <>
              {/* Inbox, Sent, Trash */}
              <Dropdown.Item
                className="d-flex align-items-center gap-2"
                onClick={() => handleBulkMoveThreads("INBOX")}
              >
                <EmailActionIcon
                  name="inbox"
                  style={{
                    fontSize: "23px",
                    width: "23px",
                  }}
                  className="flex-center p-0"
                />{" "}
                Inbox
              </Dropdown.Item>

              <Dropdown.Item
                className="d-flex align-items-center gap-2"
                onClick={() => handleBulkMoveThreads("TRASH")}
              >
                <EmailActionIcon
                  name="delete"
                  style={{
                    fontSize: "23px",
                    width: "23px",
                  }}
                  className="flex-center p-0"
                />{" "}
                Trash
              </Dropdown.Item>
              {selectedLabel !== "ALL_MAILS" ? (
                <Dropdown.Item
                  className="d-flex align-items-center gap-2"
                  onClick={() => handleBulkMoveToArchive("ARCHIVED")}
                >
                  <EmailActionIcon
                    name="archive"
                    style={{
                      fontSize: "23px",
                      width: "23px",
                    }}
                    className="flex-center p-0"
                  />{" "}
                  Archived
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  className="d-flex align-items-center gap-2"
                  onClick={() => handleBulkMoveToArchive("UNARCHIVED")}
                >
                  <EmailActionIcon
                    name="unarchive"
                    style={{
                      fontSize: "23px",
                      width: "23px",
                    }}
                    className="flex-center p-0"
                  />{" "}
                  Un Archive
                </Dropdown.Item>
              )}
            </>
          )}

          {/* Rest of the labels */}
          {labels.map((label) => {
            if (label.name === "archived_mails") {
              return null;
            }
            return (
              <Dropdown.Item
                key={label.labelId}
                className="d-flex align-items-center gap-2"
                onClick={() => handleBulkMoveThreads(label.labelId)}
              >
                <EmailActionIcon
                  name="label"
                  style={{
                    fontSize: "23px",
                    width: "23px",
                  }}
                  className="flex-center p-0"
                />{" "}
                {label.name}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
